import React from "react";
import { fmtCurrency, fmtNumber } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, CalendarX, Clock3 } from "lucide-react";

function DealRow({ d, badge }) {
  return (
    <div className="flex items-center justify-between text-sm border-b border-border pb-1.5">
      <div className="min-w-0">
        <p className="font-medium text-foreground truncate">{d.name}</p>
        <p className="text-xs text-muted-foreground">
          {d.stage} · {d.owner}
        </p>
      </div>
      <div className="text-right flex-shrink-0 ml-2">
        <p className="font-medium text-foreground">{fmtCurrency(d.amount)}</p>
        <p className="text-xs text-primary">{badge(d)}</p>
      </div>
    </div>
  );
}

function RiskCard({ title, icon: Icon, count, value, deals, badge, emptyMsg }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Icon className="w-4 h-4 text-primary" /> {title}
          {count > 0 && (
            <span className="text-xs font-normal text-muted-foreground ml-1">
              · {fmtNumber(count)} · {fmtCurrency(value)}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {deals && deals.length ? (
          <div className="space-y-2 max-h-[240px] overflow-y-auto">
            {deals.slice(0, 8).map((d) => (
              <DealRow key={d.id} d={d} badge={badge} />
            ))}
          </div>
        ) : (
          <div className="h-[100px] flex items-center justify-center text-sm text-muted-foreground text-center px-4">
            {emptyMsg}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function RiskDeals({ aging, overdue, inactive }) {
  const stale = aging?.stale_deals || [];
  const ov = overdue || {};
  const inact = inactive || {};

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="risk-deals">
      <RiskCard
        title="Overdue Deals"
        icon={CalendarX}
        count={ov.count}
        value={ov.value}
        deals={ov.deals}
        badge={(d) => `${d.days_overdue}d overdue`}
        emptyMsg="No overdue open deals — close dates look current."
      />
      <RiskCard
        title={`No Update (${inact.threshold_days ?? 30}+ days)`}
        icon={Clock3}
        count={inact.count}
        value={inact.value}
        deals={inact.deals}
        badge={(d) => `${d.days_inactive}d untouched`}
        emptyMsg="All open deals updated recently."
      />
      <RiskCard
        title="Stalled Deals (60+ days)"
        icon={AlertTriangle}
        count={aging?.stale_count}
        value={null}
        deals={stale}
        badge={(d) => `${d.age_days}d old`}
        emptyMsg="No stalled deals — healthy pipeline!"
      />
    </div>
  );
}
